import { AdVariant } from 'types/adEditorTypes';

/**
 * SYM-5921: which variant stays selected when the editor reloads its list.
 *
 * Every save refetches the whole list and re-initialises the editor, so the selection has to be
 * re-established from scratch each time. The old rule was: match on id, else the first variant with
 * the same placement, else the first variant of all.
 *
 * That silently loses a NEWLY ADDED variant. A new variant has no id until the save assigns one, so
 * the id match cannot find it, and the fallback lands on the FIRST variant of that placement. Mark
 * and Steven both hit it: add a story, edit it, save, and you are looking at the first story
 * instead. Nothing errors and no request fails, so there is no signal at all -- the same shape as
 * SYM-5873, where people edited the wrong campaign because the selector showed one the app had not
 * loaded.
 *
 * The rule this adds: a selection with no id is a variant that was just created, so the right answer
 * is the variant in the new list that was NOT in the old one. That is the only thing which identifies
 * it, since the client-side uuid is regenerated on every refetch (AdBuilder's init effect mints a
 * fresh `uniqueId()` per variant) and therefore cannot be matched either.
 *
 * Extracted from AdEditorContext so it can be asserted. In place it was unreachable by a test, which
 * is why a selection rule with an obvious hole in it survived.
 */

/** Ids present before the refetch. Anything outside this set in the new list was just created. */
export type KnownVariantIds = ReadonlySet<number>;

export const collectVariantIds = (variants: AdVariant[]): Set<number> =>
  new Set(
    variants
      .map(({ id }) => id)
      .filter((id): id is number => typeof id === 'number')
  );

export const pickSelectionAfterInit = (
  incoming: AdVariant[],
  previousSelection: AdVariant | undefined,
  previouslyKnownIds: KnownVariantIds
): AdVariant | undefined => {
  if (!incoming.length) return undefined;
  if (!previousSelection) return incoming[0];

  /**
   * Guarded on an id being present, deliberately.
   *
   * `variants.find(({ id }) => id === previousSelection.id)` with both sides undefined matches the
   * first unsaved variant in the list, which is a different ad. The same undefined-equals-undefined
   * trap was already fixed once in this codebase, in PreviewAdTemplateRequest::authorize(), where
   * null === null let a request through the ownership check.
   */
  if (previousSelection.id !== undefined) {
    const matched = incoming.find(({ id }) => id === previousSelection.id);
    if (matched) return matched;
  }

  /**
   * The selection had no id, so it was created in this editing session and the save it just went
   * through is what gave it one. It is the variant the server returned that we had never seen.
   *
   * Placement is checked too: saving can create more than one variant at once, and picking a feed
   * variant when the customer was editing a story would be the same bug wearing a different hat.
   */
  if (previousSelection.id === undefined) {
    const justCreated = incoming.find(
      ({ id, placement }) =>
        typeof id === 'number' &&
        !previouslyKnownIds.has(id) &&
        placement === previousSelection.placement
    );
    if (justCreated) return justCreated;
  }

  const samePlacement = incoming.find(
    ({ placement }) => placement === previousSelection.placement
  );

  return samePlacement ?? incoming[0];
};
